import { useState, useCallback, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import StartScreen from "./StartScreen";
import QuestionCard from "./QuestionCard";
import ResultScreen from "./ResultScreen";
import PitchBackground from "./PitchBackground";

export interface Question {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
}

export interface AnswerResult {
  questionIndex: number;
  chosen: number;
  correctIndex: number;
  correct: boolean;
}

type Screen = "start" | "quiz" | "result" | "loading" | "already-played";

const TOTAL_QUESTIONS = 10;
const QUESTION_TIME_LIMIT = 10;

const QuizGame = () => {
  const [screen, setScreen] = useState<Screen>("loading");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [score, setScore] = useState(0);
  const [previousScore, setPreviousScore] = useState<number | null>(null);
  const [streak, setStreak] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<AnswerResult[] | null>(null);
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME_LIMIT);

  useEffect(() => {
    const checkPlayed = async () => {
      try {
        const { data } = await supabase.functions.invoke("quiz-scores", {
          method: "GET",
        });
        if (data?.played) {
          setPreviousScore(data.score);
          setStreak(data.streak ?? 0);
          setScreen("already-played");
        } else {
          setStreak(data?.streak ?? 0);
          setScreen("start");
        }
      } catch {
        setScreen("start");
      }
    };
    checkPlayed();
  }, []);

  const fetchDailyQuestions = useCallback(async () => {
    setScreen("loading");
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("get-daily-quiz");
      if (fnError) throw fnError;
      setQuestions(data.questions);
      setCurrentIndex(0);
      setAnswers([]);
      setScore(0);
      setResults(null);
      setTimeLeft(QUESTION_TIME_LIMIT);
      lockedIndexRef.current = -1;
      setScreen("quiz");
    } catch (err) {
      console.error("Failed to fetch daily quiz:", err);
      setError("Failed to load today's quiz. Please try again.");
      setScreen("start");
    }
  }, []);

  const submitAnswers = useCallback(
    async (allAnswers: number[]) => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke("quiz-scores", {
          method: "POST",
          body: { answers: allAnswers },
        });
        console.log("quiz-scores POST response:", { data, fnError });
        if (data?.score !== undefined) setScore(data.score);
        if (data?.streak) setStreak(data.streak);
        if (data?.results && Array.isArray(data.results)) {
          setResults(data.results);
        } else {
          // Fallback: build results client-side from questions + answers
          const fallback: AnswerResult[] = questions.map((q, i) => ({
            questionIndex: i,
            chosen: allAnswers[i],
            correctIndex: q.correctIndex,
            correct: allAnswers[i] === q.correctIndex,
          }));
          setResults(fallback);
          setScore(fallback.filter((r) => r.correct).length);
        }
        setScreen("result");
      } catch (err) {
        console.error("Failed to save score:", err);
        const fallback: AnswerResult[] = questions.map((q, i) => ({
          questionIndex: i,
          chosen: allAnswers[i],
          correctIndex: q.correctIndex,
          correct: allAnswers[i] === q.correctIndex,
        }));
        setResults(fallback);
        setScore(fallback.filter((r) => r.correct).length);
        setScreen("result");
      }
    },
    [questions]
  );

  const lockedIndexRef = useRef<number>(-1);

  const handleAnswer = useCallback(
    (chosenIndex: number) => {
      // Lock this question so concurrent calls (timer + tap) cannot both record
      if (lockedIndexRef.current === currentIndex) return;
      lockedIndexRef.current = currentIndex;

      let nextAnswers: number[] = [];
      setAnswers((prev) => {
        // Extra safety: never exceed TOTAL_QUESTIONS
        if (prev.length >= TOTAL_QUESTIONS) {
          nextAnswers = prev;
          return prev;
        }
        nextAnswers = [...prev, chosenIndex];
        return nextAnswers;
      });

      setTimeout(() => {
        if (nextAnswers.length >= TOTAL_QUESTIONS) {
          submitAnswers(nextAnswers.slice(0, TOTAL_QUESTIONS));
        } else {
          setCurrentIndex((i) => (i === currentIndex ? i + 1 : i));
        }
      }, 1200);
    },
    [currentIndex, submitAnswers]
  );

  useEffect(() => {
    if (screen !== "quiz") return;

    setTimeLeft(QUESTION_TIME_LIMIT);
    const interval = window.setInterval(() => {
      setTimeLeft((previous) => {
        if (previous <= 1) {
          window.clearInterval(interval);
          handleAnswer(-1);
          return 0;
        }
        return previous - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [screen, currentIndex, handleAnswer]);

  // Get the server-verified result for the current question (if available)
  const currentResult = results
    ? results.find((r) => r.questionIndex === currentIndex)
    : undefined;

  const isQuizFlow = screen === "quiz" || screen === "result" || screen === "already-played";

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">
      {!isQuizFlow && (
        <Helmet>
          <title>Daily Football Quiz – 10 Questions. New Every Day.</title>
          <meta
            name="description"
            content="Play the daily football quiz with 10 new questions every day covering Premier League, Champions League, international football, and more."
          />
          <link rel="canonical" href="https://www.dailyfootballquiz.com/" />
        </Helmet>
      )}
      {isQuizFlow && (
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
      )}
      {screen === "start" && <PitchBackground />}
      <div className="relative z-10 w-full flex items-center justify-center">
      {screen === "loading" && (
        <div className="text-center animate-pulse">
          <p className="text-muted-foreground text-lg">Loading...</p>
        </div>
      )}
      {screen === "start" && (
        <StartScreen onStart={fetchDailyQuestions} error={error} streak={streak} />
      )}
      {screen === "already-played" && previousScore !== null && (
        <ResultScreen
          score={previousScore}
          total={TOTAL_QUESTIONS}
          streak={streak}
          alreadyPlayed
        />
      )}
      {screen === "quiz" && questions[currentIndex] && (
        <QuestionCard
          key={currentIndex}
          question={questions[currentIndex]}
          questionNumber={currentIndex + 1}
          totalQuestions={TOTAL_QUESTIONS}
          timeLeft={timeLeft}
          onAnswer={handleAnswer}
        />
      )}
      {screen === "result" && (
        <ResultScreen
          score={score}
          total={TOTAL_QUESTIONS}
          streak={streak}
          results={results ?? undefined}
        />
      )}
      </div>
    </div>
  );
};

export default QuizGame;
